import type { ShouldRevalidateFunction } from "react-router";
import { isProfileReorderAction } from "./is-profile-reorder-action";

/**
 * API route prefixes whose mutations affect storefront layout data
 * (cart count, delivery address, profile/session user).  When a fetcher
 * posts to one of these, the storefront loader must re-run so checkout
 * and the header reflect the latest state.
 */
const MUTATION_API_PREFIXES = [
	"/api/cart",
	"/api/delivery-address",
	"/api/profile",
] as const;

/**
 * Non-GET posts that must not revalidate the storefront layout.
 * Checked before MUTATION_API_PREFIXES so `/api/delivery-address-dismissed`
 * is not treated as a layout-affecting delivery-address mutation.
 */
const SKIP_LAYOUT_MUTATION_PATHS = [
	"/api/favorites",
	"/api/vendors-filtered",
	"/api/delivery-address-dismissed",
] as const;

function isNonGetMethod(formMethod: string | undefined): boolean {
	return Boolean(formMethod && formMethod !== "GET");
}

function isReorderWrittenResult(actionResult: unknown): boolean {
	return (
		typeof actionResult === "object" &&
		actionResult !== null &&
		"status" in actionResult &&
		(actionResult as { status: unknown }).status === "written"
	);
}

function shouldSkipLayoutMutation(
	formAction: string | undefined,
	formMethod: string | undefined,
	actionResult: unknown,
): boolean {
	if (!formAction || !isNonGetMethod(formMethod)) {
		return false;
	}
	if (isProfileReorderAction(formAction)) {
		return !isReorderWrittenResult(actionResult);
	}
	return SKIP_LAYOUT_MUTATION_PATHS.some((path) => formAction.includes(path));
}

function isLayoutAffectingMutation(
	formAction: string | undefined,
	formMethod: string | undefined,
): boolean {
	if (!formAction || !isNonGetMethod(formMethod)) {
		return false;
	}
	return MUTATION_API_PREFIXES.some((prefix) => formAction.includes(prefix));
}

function isStorefrontPath(pathname: string): boolean {
	return (
		pathname === "/" ||
		pathname.startsWith("/products") ||
		pathname.startsWith("/product/")
	);
}

/**
 * Revalidation guard for the storefront layout route (`_storefront.tsx`).
 *
 * The storefront loader fetches cart, user, customer and feature-flag data.
 * We keep the optimization that skips revalidation for **GET navigations**
 * between product pages (the layout data does not depend on product filters
 * or child-route params).
 *
 * We explicitly **allow** revalidation when:
 *   - A form mutation (POST/PUT/DELETE) targets a cart, delivery-address,
 *     or profile API route, because these change data the layout provides.
 *   - An `actionResult` is present, meaning a server action just completed.
 *   - `useRevalidator` fires (same URL, defaultShouldRevalidate is true).
 *
 * Favorites mutations and the pharmacy filtered-list POST are still skipped --
 * they do not affect the storefront loader. A reorder `warning` is also skipped
 * so the takeover dialog can open without a layout reload; a `written` reorder
 * revalidates so the cart drawer receives the new `deliveryMethodUuid`. Landing
 * `/products` from `/profile` also revalidates for that same cart write.
 */
export const storefrontShouldRevalidate: ShouldRevalidateFunction = ({
	formAction,
	formMethod,
	actionResult,
	currentUrl,
	nextUrl,
	defaultShouldRevalidate,
}) => {
	if (shouldSkipLayoutMutation(formAction, formMethod, actionResult)) {
		return false;
	}

	// Always revalidate when a server action just completed and produced a
	// result — the loader data may have changed as a side-effect.
	if (actionResult !== undefined) {
		return true;
	}

	// Cart, delivery-address and profile mutations must trigger a loader
	// refresh so the header badge count, user name and checkout see fresh data.
	if (isLayoutAffectingMutation(formAction, formMethod)) {
		return true;
	}

	// Honour explicit revalidation (e.g. useRevalidator) so that flows like
	// stock-validation checkout receive fresh data. When useRevalidator fires,
	// currentUrl and nextUrl are identical and defaultShouldRevalidate is true.
	if (currentUrl.href === nextUrl.href && defaultShouldRevalidate) {
		return true;
	}

	// Reorder lands /products from /profile. The layout must reload so the cart
	// drawer receives the written deliveryMethodUuid; the product-page skip
	// below would otherwise keep the previous basket.
	if (
		!formAction &&
		currentUrl.pathname.startsWith("/profile") &&
		isStorefrontPath(nextUrl.pathname)
	) {
		return true;
	}

	// When navigating within storefront child routes (e.g. product list ↔ PDP,
	// or applying product filters) the storefront layout data does not change.
	// Only revalidate when the user leaves the storefront entirely.
	// This optimisation applies only to GET navigations (no formAction).
	if (
		!formAction &&
		isStorefrontPath(currentUrl.pathname) &&
		isStorefrontPath(nextUrl.pathname)
	) {
		return false;
	}

	return defaultShouldRevalidate;
};
